import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import {
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Check,
  MessageSquare,
  AlertCircle,
  Loader2,
  Send,
  ThumbsUp,
  ThumbsDown,
  Clock,
} from "lucide-react";

const interpolateKF = (keyframes, time) => {
  if (!keyframes || keyframes.length === 0) return null;
  if (keyframes.length === 1) return { x: keyframes[0].x, y: keyframes[0].y };
  const sorted = [...keyframes].sort((a, b) => a.time - b.time);
  if (time <= sorted[0].time) return { x: sorted[0].x, y: sorted[0].y };
  if (time >= sorted[sorted.length - 1].time)
    return { x: sorted[sorted.length - 1].x, y: sorted[sorted.length - 1].y };
  for (let i = 0; i < sorted.length - 1; i++) {
    if (time >= sorted[i].time && time <= sorted[i + 1].time) {
      const t = (time - sorted[i].time) / (sorted[i + 1].time - sorted[i].time);
      return {
        x: sorted[i].x + (sorted[i + 1].x - sorted[i].x) * t,
        y: sorted[i].y + (sorted[i + 1].y - sorted[i].y) * t,
      };
    }
  }
  return { x: sorted[sorted.length - 1].x, y: sorted[sorted.length - 1].y };
};

const OVERLAY_STYLES = {
  super: { color: "#fff", fontSize: "clamp(14px, 2.5vw, 22px)", fontWeight: "700", textShadow: "0 2px 8px rgba(0,0,0,0.8)" },
  disclaimer: { color: "#ccc", fontSize: "clamp(9px, 1.4vw, 13px)", fontWeight: "400", textShadow: "0 1px 4px rgba(0,0,0,0.9)" },
  end_frame: { color: "#fff", fontSize: "clamp(16px, 3vw, 28px)", fontWeight: "800", textShadow: "0 2px 12px rgba(0,0,0,0.7)" },
};

const formatTime = (t) => {
  const m = Math.floor(t / 60);
  const s = t % 60;
  return `${m}:${s < 10 ? "0" : ""}${s.toFixed(1)}`;
};

const ClientReviewPage = () => {
  const { token } = useParams();
  const videoRef = useRef(null);
  const progressRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [feedback, setFeedback] = useState([]);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [activeScene, setActiveScene] = useState(0);
  const [authorName, setAuthorName] = useState("");
  const [commentText, setCommentText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [overallStatus, setOverallStatus] = useState("pending");

  // Load review data
  useEffect(() => {
    const load = async () => {
      try {
        const [reviewRes, fbRes] = await Promise.all([
          axios.get(`${API}/review/${token}`),
          axios.get(`${API}/review/${token}/feedback`),
        ]);
        setData(reviewRes.data);
        setFeedback(fbRes.data || []);
        setOverallStatus(reviewRes.data.overall_status || "pending");
      } catch (err) {
        setError(err.response?.data?.detail || "Invalid or expired review link");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  // Video time tracking
  useEffect(() => {
    const vid = videoRef.current;
    if (!vid) return;
    const onTime = () => setCurrentTime(vid.currentTime);
    const onMeta = () => setVideoDuration(vid.duration || 0);
    const onEnd = () => setPlaying(false);
    vid.addEventListener("timeupdate", onTime);
    vid.addEventListener("loadedmetadata", onMeta);
    vid.addEventListener("ended", onEnd);
    return () => {
      vid.removeEventListener("timeupdate", onTime);
      vid.removeEventListener("loadedmetadata", onMeta);
      vid.removeEventListener("ended", onEnd);
    };
  }, [data]);

  // Find current scene
  useEffect(() => {
    if (!data?.scenes) return;
    for (let i = 0; i < data.scenes.length; i++) {
      if (currentTime >= data.scenes[i].start_time && currentTime < data.scenes[i].end_time) {
        setActiveScene(i);
        return;
      }
    }
  }, [currentTime, data]);

  // Parse crop positions into a lookup
  const cropMap = useMemo(() => {
    if (!data?.crop_positions) return {};
    const map = {};
    data.crop_positions.forEach((cp) => {
      if (!map[cp.scene_id]) map[cp.scene_id] = {};
      map[cp.scene_id][cp.aspect_ratio] = {
        x: Math.round((cp.center_x ?? 0.5) * 100),
        y: Math.round((cp.center_y ?? 0.5) * 100),
        keyframes: (cp.keyframes || []).map((kf) => ({
          time: kf.time,
          x: Math.round((kf.x ?? 0.5) * 100),
          y: Math.round((kf.y ?? 0.5) * 100),
        })),
      };
    });
    return map;
  }, [data]);

  // Current crop position (with keyframe interpolation)
  const cropPos = useMemo(() => {
    if (!data?.scenes?.[activeScene]) return { x: 50, y: 50 };
    const scene = data.scenes[activeScene];
    const ar = data.aspect_ratio;
    const cpData = cropMap[scene.id]?.[ar];
    if (!cpData) return { x: 50, y: 50 };
    if (cpData.keyframes?.length >= 2) {
      const interp = interpolateKF(cpData.keyframes, currentTime);
      if (interp) return interp;
    }
    return { x: cpData.x, y: cpData.y };
  }, [activeScene, currentTime, data, cropMap]);

  // Video transform for crop viewport
  const videoTransform = useMemo(() => {
    if (!data) return { width: "100%", height: "100%", left: "0%", top: "0%" };
    const [arW, arH] = (data.aspect_ratio || "16:9").split(":").map(Number);
    const targetRatio = arW / arH;
    const srcRatio = (data.project.video_width || 1920) / (data.project.video_height || 1080);

    if (targetRatio < srcRatio) {
      const videoW = (srcRatio / targetRatio) * 100;
      const excess = videoW - 100;
      return { width: `${videoW}%`, height: "100%", left: `${-excess * (cropPos.x / 100)}%`, top: "0%" };
    } else {
      const videoH = (targetRatio / srcRatio) * 100;
      const excess = videoH - 100;
      return { width: "100%", height: `${videoH}%`, left: "0%", top: `${-excess * (cropPos.y / 100)}%` };
    }
  }, [data, cropPos]);

  // Active overlays
  const activeOverlays = useMemo(() => {
    if (!data?.graphics) return [];
    return data.graphics.filter((o) => currentTime >= o.start_time && currentTime <= o.end_time);
  }, [data, currentTime]);

  // Video URL
  const videoUrl = useMemo(() => {
    if (!data) return null;
    return `${API}/projects/${data.project.id}/video/play?token=${data.video_token}`;
  }, [data]);

  // Aspect ratio for viewport
  const [arW, arH] = useMemo(() => {
    if (!data?.aspect_ratio) return [16, 9];
    return data.aspect_ratio.split(":").map(Number);
  }, [data]);

  const togglePlay = useCallback(() => {
    const vid = videoRef.current;
    if (!vid) return;
    if (playing) vid.pause();
    else vid.play();
    setPlaying(!playing);
  }, [playing]);

  const seekTo = useCallback((t) => {
    if (videoRef.current) videoRef.current.currentTime = t;
    setCurrentTime(t);
  }, []);

  // Submit feedback
  const submitFeedback = async (sceneId, status) => {
    if (!authorName.trim()) {
      setAuthorName("Client");
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/review/${token}/feedback`, {
        scene_id: sceneId,
        timestamp: parseFloat(currentTime.toFixed(2)),
        status,
        comment: commentText,
        author_name: authorName.trim() || "Client",
      });
      setFeedback((prev) => [...prev, res.data]);
      setCommentText("");
    } catch {}
    setSubmitting(false);
  };

  // Overall approval
  const submitApproval = async (status) => {
    try {
      await axios.post(`${API}/review/${token}/approve`, { status });
      setOverallStatus(status);
    } catch {}
  };

  // Per-scene feedback summary
  const sceneFeedbackMap = useMemo(() => {
    const map = {};
    feedback.forEach((fb) => {
      if (fb.scene_id) {
        if (!map[fb.scene_id]) map[fb.scene_id] = [];
        map[fb.scene_id].push(fb);
      }
    });
    return map;
  }, [feedback]);

  const getSceneStatus = (sceneId) => {
    const fbs = sceneFeedbackMap[sceneId] || [];
    const last = [...fbs].reverse().find((f) => f.status === "approved" || f.status === "changes_requested");
    return last?.status || null;
  };

  const progress = videoDuration > 0 ? (currentTime / videoDuration) * 100 : 0;

  // ── LOADING / ERROR STATES ──
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="fixed inset-0 bg-gradient-hero -z-10" />
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-400/50" />
        <span className="text-sm text-cyan-200/30">Loading review...</span>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-8">
      <div className="fixed inset-0 bg-gradient-hero -z-10" />
      <div className="text-center">
        <div className="w-16 h-16 mx-auto border border-red-400/30 rounded-sm flex items-center justify-center mb-6">
          <AlertCircle className="w-8 h-8 text-red-400" />
        </div>
        <h1 className="text-xl font-['Rajdhani'] tracking-wide mb-2 text-white">INVALID REVIEW LINK</h1>
        <p className="text-cyan-200/40">{error}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen" data-testid="client-review-page">
      <div className="fixed inset-0 bg-gradient-hero -z-10" />

      {/* Header */}
      <header className="border-b border-cyan-500/10 backdrop-blur-sm bg-[#020a14]/60">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/assets/logo.png" alt="MasteringMate" className="h-6" />
            <div className="w-px h-6 bg-cyan-500/15" />
            <div>
              <h1 className="text-sm font-['Rajdhani'] tracking-wider text-cyan-200/70">
                CLIENT REVIEW — {data.project.name}
              </h1>
              <p className="text-[10px] text-cyan-400/30 font-mono">
                {data.aspect_ratio} crop preview
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {overallStatus === "approved" && (
              <span className="px-3 py-1 bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 text-xs font-['Rajdhani'] tracking-wider rounded-sm" data-testid="status-approved">
                APPROVED
              </span>
            )}
            {overallStatus === "changes_requested" && (
              <span className="px-3 py-1 bg-amber-500/15 border border-amber-400/30 text-amber-300 text-xs font-['Rajdhani'] tracking-wider rounded-sm" data-testid="status-changes">
                CHANGES REQUESTED
              </span>
            )}
            {overallStatus === "pending" && (
              <span className="px-3 py-1 bg-cyan-500/10 border border-cyan-500/20 text-cyan-300/50 text-xs font-['Rajdhani'] tracking-wider rounded-sm" data-testid="status-pending">
                PENDING REVIEW
              </span>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Video Preview */}
        <div className="flex justify-center mb-8">
          <div
            className="relative overflow-hidden bg-black shadow-[0_0_60px_rgba(0,180,255,0.06)] border border-white/[0.06] rounded-sm"
            style={{
              aspectRatio: `${arW}/${arH}`,
              maxHeight: "55vh",
              height: "55vh",
            }}
            data-testid="review-viewport"
          >
            <video
              ref={videoRef}
              src={videoUrl}
              className="absolute object-cover"
              style={videoTransform}
              playsInline
              data-testid="review-video"
            />
            {/* Overlays */}
            {activeOverlays.map((ov) => {
              const style = OVERLAY_STYLES[ov.type] || OVERLAY_STYLES.super;
              const fadeIn = Math.min(1, (currentTime - ov.start_time) / 0.3);
              const fadeOut = Math.min(1, (ov.end_time - currentTime) / 0.3);
              return (
                <div
                  key={ov.id}
                  className="absolute pointer-events-none"
                  style={{
                    left: `${ov.position?.x ?? 50}%`,
                    top: `${ov.position?.y ?? 50}%`,
                    transform: "translate(-50%, -50%)",
                    opacity: Math.min(fadeIn, fadeOut),
                    ...style,
                    maxWidth: "80%",
                    textAlign: "center",
                  }}
                >
                  {ov.content}
                </div>
              );
            })}
            <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-black/60 rounded-sm">
              <span className="text-[9px] text-white/40 font-mono">{formatTime(currentTime)}</span>
            </div>
          </div>
        </div>

        {/* Transport */}
        <div className="max-w-xl mx-auto mb-10" data-testid="review-transport">
          <div
            ref={progressRef}
            className="relative h-1.5 bg-white/10 rounded-full cursor-pointer mb-4 group"
            onClick={(e) => {
              const rect = progressRef.current.getBoundingClientRect();
              seekTo(Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)) * videoDuration);
            }}
            data-testid="review-progress"
          >
            <div className="absolute h-full bg-cyan-400/70 rounded-full" style={{ width: `${progress}%` }} />
            {data.scenes.map((s, i) => (
              <div key={i} className="absolute top-0 w-px h-full bg-white/20" style={{ left: `${(s.start_time / videoDuration) * 100}%` }} />
            ))}
          </div>
          <div className="flex items-center justify-center gap-3">
            <button onClick={() => seekTo(Math.max(0, data.scenes[Math.max(0, activeScene - 1)]?.start_time ?? 0))} className="p-2 text-white/40 hover:text-white transition-colors" data-testid="review-prev">
              <SkipBack className="w-4 h-4" />
            </button>
            <button onClick={togglePlay} className="p-3 rounded-full bg-white/10 text-white hover:bg-white/20 transition-all" data-testid="review-play-btn">
              {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            </button>
            <button onClick={() => { const n = Math.min(data.scenes.length - 1, activeScene + 1); seekTo(data.scenes[n]?.start_time ?? 0); }} className="p-2 text-white/40 hover:text-white transition-colors" data-testid="review-next">
              <SkipForward className="w-4 h-4" />
            </button>
            <span className="text-xs text-white/40 font-mono ml-2">{formatTime(currentTime)} / {formatTime(videoDuration)}</span>
          </div>
        </div>

        {/* Scene-by-scene Feedback */}
        <div className="mb-10" data-testid="scene-feedback-section">
          <h2 className="text-xs text-cyan-400/50 tracking-widest uppercase mb-4">Scene Review</h2>
          <div className="space-y-3">
            {data.scenes.map((scene, i) => {
              const sceneStatus = getSceneStatus(scene.id);
              const isActive = i === activeScene;
              const sceneFb = sceneFeedbackMap[scene.id] || [];
              return (
                <div
                  key={scene.id}
                  className={`p-4 rounded-sm border transition-all cursor-pointer ${
                    isActive
                      ? "bg-cyan-500/[0.08] border-cyan-500/25"
                      : "bg-cyan-500/[0.02] border-cyan-500/10 hover:border-cyan-500/20"
                  }`}
                  onClick={() => seekTo(scene.start_time)}
                  data-testid={`review-scene-${i}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-cyan-500/40 font-mono">{String(i + 1).padStart(2, "0")}</span>
                      <span className="text-sm text-cyan-200/70 font-['Rajdhani'] tracking-wide">
                        Scene {i + 1}
                      </span>
                      <span className="text-[10px] text-cyan-200/25 font-mono">
                        {scene.start_time.toFixed(1)}s — {scene.end_time.toFixed(1)}s
                      </span>
                      {isActive && (
                        <span className="px-1.5 py-0.5 bg-cyan-500/20 text-[9px] text-cyan-300 rounded-sm font-mono">NOW</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {sceneStatus === "approved" && (
                        <span className="flex items-center gap-1 px-2 py-1 bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 text-[10px] rounded-sm">
                          <Check className="w-3 h-3" /> Approved
                        </span>
                      )}
                      {sceneStatus === "changes_requested" && (
                        <span className="flex items-center gap-1 px-2 py-1 bg-amber-500/15 border border-amber-400/30 text-amber-300 text-[10px] rounded-sm">
                          <AlertCircle className="w-3 h-3" /> Changes
                        </span>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); submitFeedback(scene.id, "approved"); }}
                        className="p-1.5 rounded-sm text-emerald-400/40 hover:text-emerald-300 hover:bg-emerald-500/10 transition-all"
                        title="Approve this scene"
                        data-testid={`approve-scene-${i}`}
                      >
                        <ThumbsUp className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); submitFeedback(scene.id, "changes_requested"); }}
                        className="p-1.5 rounded-sm text-amber-400/40 hover:text-amber-300 hover:bg-amber-500/10 transition-all"
                        title="Request changes for this scene"
                        data-testid={`reject-scene-${i}`}
                      >
                        <ThumbsDown className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  {/* Scene comments */}
                  {sceneFb.length > 0 && (
                    <div className="ml-9 space-y-1.5 mt-2">
                      {sceneFb.map((fb) => (
                        <div key={fb.id} className="flex items-start gap-2 text-[11px]">
                          <Clock className="w-3 h-3 text-cyan-500/30 mt-0.5 shrink-0" />
                          <span className="text-cyan-200/30 font-mono shrink-0">{fb.timestamp != null ? formatTime(fb.timestamp) : "—"}</span>
                          <span className="text-cyan-200/50">{fb.author_name}:</span>
                          <span className="text-cyan-200/70">{fb.comment || fb.status.replace("_", " ")}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Comment input */}
        <div className="max-w-2xl mx-auto mb-10 p-5 card-glass rounded-sm" data-testid="comment-form">
          <h3 className="text-xs text-cyan-400/50 tracking-widest uppercase mb-4">Add Feedback</h3>
          <div className="grid grid-cols-[1fr_2fr] gap-3 mb-3">
            <input
              value={authorName}
              onChange={(e) => setAuthorName(e.target.value)}
              placeholder="Your name"
              className="h-10 px-3 rounded-sm bg-cyan-500/[0.04] border border-cyan-500/15 text-sm text-cyan-200/80 placeholder:text-cyan-300/20 focus:border-cyan-400/40 outline-none"
              data-testid="reviewer-name"
            />
            <div className="flex items-center gap-2">
              <input
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && commentText.trim() && submitFeedback(data.scenes[activeScene]?.id, "comment")}
                placeholder={`Comment on Scene ${activeScene + 1} at ${formatTime(currentTime)}...`}
                className="flex-1 h-10 px-3 rounded-sm bg-cyan-500/[0.04] border border-cyan-500/15 text-sm text-cyan-200/80 placeholder:text-cyan-300/20 focus:border-cyan-400/40 outline-none"
                data-testid="review-comment-input"
              />
              <button
                onClick={() => commentText.trim() && submitFeedback(data.scenes[activeScene]?.id, "comment")}
                disabled={!commentText.trim() || submitting}
                className="h-10 px-4 rounded-sm bg-cyan-500/10 border border-cyan-500/20 text-cyan-300/60 hover:text-cyan-200 hover:bg-cyan-500/20 disabled:opacity-30 transition-all"
                data-testid="submit-comment-btn"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
          <p className="text-[10px] text-cyan-400/25">
            <MessageSquare className="w-3 h-3 inline mr-1" />
            Comment will be attached to Scene {activeScene + 1} at {formatTime(currentTime)}
          </p>
        </div>

        {/* Overall Approval */}
        <div className="max-w-2xl mx-auto text-center pb-16" data-testid="overall-approval">
          <div className="w-px h-8 bg-cyan-500/10 mx-auto mb-6" />
          <h3 className="text-xs text-cyan-400/40 tracking-widest uppercase mb-6">Overall Decision</h3>
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={() => submitApproval("approved")}
              className={`flex items-center gap-2 px-8 py-3 rounded-sm font-['Rajdhani'] tracking-wider text-sm transition-all ${
                overallStatus === "approved"
                  ? "bg-emerald-500 text-black"
                  : "bg-emerald-500/10 border border-emerald-400/30 text-emerald-300 hover:bg-emerald-500/20"
              }`}
              data-testid="approve-all-btn"
            >
              <ThumbsUp className="w-4 h-4" /> APPROVE ALL
            </button>
            <button
              onClick={() => submitApproval("changes_requested")}
              className={`flex items-center gap-2 px-8 py-3 rounded-sm font-['Rajdhani'] tracking-wider text-sm transition-all ${
                overallStatus === "changes_requested"
                  ? "bg-amber-500 text-black"
                  : "bg-amber-500/10 border border-amber-400/30 text-amber-300 hover:bg-amber-500/20"
              }`}
              data-testid="request-changes-btn"
            >
              <ThumbsDown className="w-4 h-4" /> REQUEST CHANGES
            </button>
          </div>
          {overallStatus !== "pending" && (
            <p className="text-xs text-cyan-200/30 mt-4">
              Your decision has been recorded. The editor has been notified.
            </p>
          )}
        </div>
      </main>
    </div>
  );
};

export default ClientReviewPage;
